/**
 * <presale-popup> — pre-sale info modal. See sections/presale-popup.liquid.
 *
 * The "is this a pre-sale product?" rule has ONE home: the section's Liquid (product in the collection
 * picked in the section settings). This file never decides it — it only asks.
 *
 * TRIGGERS — at most once per product per page view ("rule A": a new page starts fresh):
 *   1. PDP load    — the Liquid marks a matching PDP with data-product-id; open shortly after paint.
 *   2. Any ADD     — watch the browser's network log for /cart/add (PerformanceObserver, read-only: no
 *                    fetch/XHR wrapping — Intelligems + UpCart + Shopify already wrap those). Then:
 *                    re-read /cart.js, diff against the last snapshot (keyed by line key) to see which
 *                    products actually went UP, and ask the section — via the Section Rendering API,
 *                    /products/<handle>?sections=<id> — whether each is a match.
 *
 *   3. MANUAL      — any trigger button pointed at "presale-popup" (none on the site since the PDP's
 *                    PRE-SALE row button was removed 2026-09-12). ALWAYS opens; rules 1–2 and the
 *                    once-per-page memory don't apply (see MANUAL_TRIGGER below).
 *
 *   Why the network log and not UpCart's events (live-tested 2026-09-10): UpCart's AddedToCart misses
 *   upsell adds and background adds (e.g. assets/custom.js complementary products). Every add hits
 *   /cart/add; the drawer's "+" hits /cart/change — so quantity bumps are ignored for free, as agreed.
 *   /cart/change|update|clear still refresh the snapshot so a later diff stays honest.
 *
 * Fail-closed everywhere: any fetch/parse problem = no popup, never a broken page.
 *
 * Closes on: the X, the CTA, the overlay, Escape — anything carrying [data-presale-popup-close].
 * ⚠️ Escape is swallowed while open (window, CAPTURE phase — runs before anyone else's listener), so it
 * closes ONLY this popup; UpCart also closes its drawer on Escape and it sits right behind us on add.
 *
 * DEV: ?presale_popup_preview=1 forces it open (the section must be enabled — off renders nothing).
 */
(function () {
  if (customElements.get('presale-popup')) return;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var DURATION = 350; // matches the 0.35s CSS transition
  var PDP_OPEN_DELAY = 600; // let the product page paint first
  var ADD_OPEN_DELAY = 450; // at least UpCart's drawer slide-in — land on a settled drawer
  var ADD_RE = /\/cart\/add(\.js)?$/;
  var MUTATE_RE = /\/cart\/(change|update|clear)(\.js)?$/;
  // Manual trigger: any shared ss-modal-trigger pointed at "presale-popup". The PDP's PRE-SALE row button
  // was removed 2026-09-12 — this listener is kept so one can be re-added with no JS change. ss-modal.js
  // finds no "presale-popup" template and steps
  // aside, so this is the only listener that acts on it. Opens ALWAYS: no collection / once-per-page rules.
  var MANUAL_TRIGGER = '[data-ss-modal-open="presale-popup"]';

  class PresalePopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.presale-popup__dialog');
      if (!this.dialog) return;

      this.isOpen = false;
      this.locked = false; // did WE add the scroll lock (so we only remove our own)
      this.lastFocused = null;
      this.hideTimer = null;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onManualTrigger = this.onManualTrigger.bind(this);
      this.addEventListener('click', this.onClick);
      // Before the editor branch on purpose — a manual trigger works in the theme editor too.
      document.addEventListener('click', this.onManualTrigger);

      // THEME EDITOR: open while the section is selected in the sidebar, close on deselect — so the
      // merchant sees what they're editing. Replaces the storefront logic entirely (no triggers).
      if (window.Shopify && window.Shopify.designMode) {
        this.initDesignMode();
        return;
      }

      if (this.getAttribute('data-triggers') === 'true') this.initTriggers();

      if (new URLSearchParams(window.location.search).has('presale_popup_preview')) this.open();
    }

    disconnectedCallback() {
      // The editor replaces this element on every settings change — never leave the page locked.
      window.removeEventListener('keydown', this.onKeydown, true);
      document.removeEventListener('click', this.onManualTrigger);
      if (this.locked) document.documentElement.classList.remove('ss-scroll-lock');
      clearTimeout(this.hideTimer);
      if (this.observer) this.observer.disconnect();
      if (this._onPageShow) window.removeEventListener('pageshow', this._onPageShow);
      document.removeEventListener('shopify:section:select', this._onEditorSelect);
      document.removeEventListener('shopify:section:deselect', this._onEditorDeselect);
    }

    /* ---------------- Open / close ---------------- */

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      clearTimeout(this.hideTimer); // reopened mid-close

      this.lastFocused = document.activeElement;
      this.hidden = false;
      // Force a style flush so the transition runs from the closed state (we were display:none).
      void this.offsetWidth;
      this.classList.add('is-open');

      if (!document.documentElement.classList.contains('ss-scroll-lock')) {
        document.documentElement.classList.add('ss-scroll-lock');
        this.locked = true;
      }

      // Focus the dialog itself (tabindex="-1"), not the X — a11y focus move without a ring flash.
      this.dialog.focus({ preventScroll: true });
      window.addEventListener('keydown', this.onKeydown, true);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      window.removeEventListener('keydown', this.onKeydown, true);

      if (this.locked) {
        document.documentElement.classList.remove('ss-scroll-lock');
        this.locked = false;
      }

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus({ preventScroll: true });
      }

      // Back to display:none after the fade, unless reopened meanwhile.
      var self = this;
      this.hideTimer = setTimeout(function () {
        if (!self.isOpen) self.hidden = true;
      }, DURATION);
    }

    onClick(e) {
      if (e.target.closest('[data-presale-popup-close]')) this.close();
    }

    onManualTrigger(e) {
      if (e.target.closest(MANUAL_TRIGGER)) this.open();
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.close();
        return;
      }
      if (e.key === 'Tab') this.trapFocus(e);
    }

    trapFocus(e) {
      var nodes = this.dialog.querySelectorAll(FOCUSABLE);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      // We focus the dialog container on open; it isn't in `nodes`, so snap to an edge.
      if (document.activeElement === this.dialog) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---------------- Triggers ---------------- */

    initTriggers() {
      var self = this;
      this.sectionId = this.getAttribute('data-section-id') || 'presale-popup';
      this.root = (this.getAttribute('data-root') || '/').replace(/\/$/, ''); // "" or "/en-ca"
      this.shown = {}; // product id -> true: rule A, THIS page view only
      this.matchCache = {}; // handle -> Promise<boolean>, this page view only
      this.queue = Promise.resolve(); // cart reads run one at a time, in order
      this.snapshot = this.readSnapshot();

      // 1. PDP load
      var pdpId = this.getAttribute('data-product-id');
      if (pdpId) {
        this.shown[pdpId] = true;
        setTimeout(function () {
          self.open();
        }, PDP_OPEN_DELAY);
      }

      // 2. Any add
      if (!('PerformanceObserver' in window)) return;
      this.observer = new PerformanceObserver(function (list) {
        self.onEntries(list.getEntries());
      });
      try {
        this.observer.observe({ type: 'resource' });
      } catch (err) {
        return; // very old browser — PDP trigger still works
      }

      // No Liquid snapshot (shouldn't happen) → take a baseline now, so the first add isn't misread.
      if (!this.snapshot) this.enqueue(false);

      // Back/forward cache restores a frozen page — the cart may have moved on meanwhile.
      this._onPageShow = function (e) {
        if (e.persisted) self.enqueue(false);
      };
      window.addEventListener('pageshow', this._onPageShow);
    }

    readSnapshot() {
      var el = this.querySelector('script[data-presale-cart]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent) || null;
      } catch (err) {
        return null;
      }
    }

    onEntries(entries) {
      var isAdd = false;
      var isMutation = false;
      for (var i = 0; i < entries.length; i++) {
        var type = entries[i].initiatorType;
        if (type !== 'fetch' && type !== 'xmlhttprequest') continue;
        var path;
        try {
          path = new URL(entries[i].name).pathname;
        } catch (err) {
          continue;
        }
        if (ADD_RE.test(path)) isAdd = true;
        else if (MUTATE_RE.test(path)) isMutation = true;
      }
      if (isAdd || isMutation) this.enqueue(isAdd);
    }

    enqueue(isAdd) {
      var self = this;
      var startedAt = Date.now();
      this.queue = this.queue
        .then(function () {
          return self.sync(isAdd, startedAt);
        })
        .catch(function () {}); // fail closed
    }

    // Re-read the cart, update the snapshot, and — for an add — find what went up and check it.
    sync(isAdd, startedAt) {
      var self = this;
      return fetch(this.root + '/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var prev = self.snapshot || {};
          var next = {};
          var candidates = [];
          var seen = {};

          cart.items.forEach(function (item) {
            next[item.key] = item.quantity;
            if (!isAdd || item.quantity <= (prev[item.key] || 0)) return;
            var id = String(item.product_id);
            if (seen[id] || self.shown[id]) return; // one check per product; rule A
            seen[id] = true;
            candidates.push(item);
          });

          self.snapshot = next;
          if (!candidates.length) return;

          return Promise.all(
            candidates.map(function (item) {
              return self.isMatch(item.handle).then(function (ok) {
                return ok ? item : null;
              });
            })
          ).then(function (results) {
            var matches = results.filter(Boolean);
            if (!matches.length) return;
            // Same message for every product: several matches in one add = one popup, all marked shown.
            matches.forEach(function (item) {
              self.shown[String(item.product_id)] = true;
            });
            var wait = Math.max(0, ADD_OPEN_DELAY - (Date.now() - startedAt));
            setTimeout(function () {
              self.open();
            }, wait);
          });
        });
    }

    // Asks the section itself, rendered for that product: <presale-popup data-product-match="true">?
    isMatch(handle) {
      if (!handle) return Promise.resolve(false);
      if (!this.matchCache[handle]) {
        var sectionId = this.sectionId;
        var url =
          this.root + '/products/' + encodeURIComponent(handle) + '?sections=' + encodeURIComponent(sectionId);
        this.matchCache[handle] = fetch(url)
          .then(function (r) {
            return r.ok ? r.json() : {};
          })
          .then(function (data) {
            var html = data && data[sectionId];
            return typeof html === 'string' && html.indexOf('data-product-match="true"') !== -1;
          })
          .catch(function () {
            return false;
          });
      }
      return this.matchCache[handle];
    }

    /* ---------------- Theme editor ---------------- */

    initDesignMode() {
      var self = this;
      var wrapperId = 'shopify-section-' + (this.getAttribute('data-section-id') || '');

      // `shopify:section:select` re-fires after a re-render while still selected (detail.load) —
      // that keeps the popup on screen as the merchant types, instead of vanishing per keystroke.
      this._onEditorSelect = function (e) {
        if (e.target.id === wrapperId) self.open();
      };
      this._onEditorDeselect = function (e) {
        if (e.target.id === wrapperId) self.close();
      };

      document.addEventListener('shopify:section:select', this._onEditorSelect);
      document.addEventListener('shopify:section:deselect', this._onEditorDeselect);
    }
  }

  customElements.define('presale-popup', PresalePopup);
})();
